import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { replaceUrlSilently } from '@/lib/replace-url-silently';
import { ROLE_COLORS, ROLE_LABELS } from '@/lib/role-display';
import { useAuth } from '@/store/auth';

import type { AuthSession } from '@logistech/ui-shared';

// Page publique (hors layout _authed) : le lien d'invitation reçu par email arrive ici
export const Route = createFileRoute('/accept-invitation/$token')({ component: AcceptInvitationRoute });

function AcceptInvitationRoute() {
  const { token } = Route.useParams();
  return <AcceptInvitationPage token={token} />;
}

type Role = AuthSession['user']['role'];

interface InvitationPreview {
  email: string;
  role: Role;
  tenantName: string | null;
  invitedBy: string | null;
  expiresAt: string;
}

// 404/410 sont des états de la page, pas des erreurs : pas de toast global « Erreur de chargement »
type PreviewResult = { state: 'ok'; invitation: InvitationPreview } | { state: 'notFound' } | { state: 'gone' };

const formSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Prénom requis'),
    lastName: z.string().trim().min(1, 'Nom requis'),
    password: z.string().min(10, 'Au moins 10 caractères'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Les mots de passe ne correspondent pas',
  });

type FormValues = z.infer<typeof formSchema>;
type FieldErrors = Partial<Record<keyof FormValues, string>>;

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;

// La politique de mot de passe renvoie ses règles non respectées en `details: string[]`
const acceptErrorMessage = (err: unknown): string => {
  const details = (err as { response?: { data?: { error?: { details?: unknown } } } })?.response?.data?.error?.details;
  if (Array.isArray(details) && details.length && details.every((d) => typeof d === 'string')) return details.join(' · ');
  return apiErrorMessage(err, 'Impossible d’accepter l’invitation');
};

function InvitationShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-primary-500">LogisTech-LGT</CardTitle>
          <p className="text-sm text-muted-foreground">{title}</p>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

export function AcceptInvitationPage({ token }: { token: string }) {
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [values, setValues] = useState<FormValues>({ firstName: '', lastName: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState<FieldErrors>({});

  // SECURITY-FIX 8.4.2 — le token (lu une fois depuis l'URL, gardé en mémoire) sort de la barre d'adresse :
  // ni historique du navigateur, ni capture d'écran, ni Referer. Le routeur n'est pas notifié : /accept-invitation
  // ne correspond à aucune route, la page serait démontée (« Not Found ») avant l'appel API.
  useEffect(() => {
    replaceUrlSilently('/accept-invitation');
  }, []);

  const preview = useQuery({
    queryKey: ['public-invitation', token],
    queryFn: async (): Promise<PreviewResult> => {
      try {
        const res = await api.get<InvitationPreview>(`/public/invitations/${encodeURIComponent(token)}`);
        return { state: 'ok', invitation: res.data };
      } catch (err) {
        if (statusOf(err) === 404) return { state: 'notFound' };
        if (statusOf(err) === 410) return { state: 'gone' };
        throw err;
      }
    },
    retry: false,
    gcTime: 0,
  });

  const accept = useMutation({
    mutationFn: async (v: FormValues) =>
      (await api.post<AuthSession>('/public/invitations/accept', {
        token,
        firstName: v.firstName.trim(),
        lastName: v.lastName.trim(),
        password: v.password,
      })).data,
    onSuccess: (session) => {
      setSession(session);
      void nav({ to: '/' });
    },
    onError: (err) => toast.error(acceptErrorMessage(err)),
  });

  const set = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [field]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormValues;
        fieldErrors[key] ??= issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    accept.mutate(parsed.data);
  };

  if (preview.isPending) {
    return <InvitationShell title="Invitation"><p className="text-sm text-muted-foreground">Chargement…</p></InvitationShell>;
  }

  if (preview.isError || preview.data.state !== 'ok') {
    const gone = preview.data?.state === 'gone';
    return (
      <InvitationShell title={gone ? 'Invitation expirée ou déjà utilisée' : 'Invitation introuvable'}>
        <div className="space-y-4">
          <p className="text-sm">
            {gone
              ? 'Ce lien n’est plus valide. Contactez l’administrateur de votre organisation pour recevoir une nouvelle invitation.'
              : preview.isError
                ? 'Impossible de vérifier l’invitation pour le moment. Réessayez plus tard.'
                : 'Ce lien d’invitation est invalide. Vérifiez qu’il est complet ou demandez une nouvelle invitation.'}
          </p>
          <Button className="w-full" variant="outline" onClick={() => void nav({ to: '/login' })}>
            Retour à la connexion
          </Button>
        </div>
      </InvitationShell>
    );
  }

  const inv = preview.data.invitation;

  return (
    <InvitationShell title="Rejoindre votre équipe">
      <dl className="mb-6 space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Organisation</dt>
          <dd className="font-medium">{inv.tenantName ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Email</dt>
          <dd className="font-medium">{inv.email}</dd>
        </div>
        <div className="flex justify-between gap-2 items-center">
          <dt className="text-muted-foreground">Rôle</dt>
          <dd><Badge className={ROLE_COLORS[inv.role]}>{ROLE_LABELS[inv.role]}</Badge></dd>
        </div>
        {inv.invitedBy && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">Invité par</dt>
            <dd className="font-medium">{inv.invitedBy}</dd>
          </div>
        )}
      </dl>

      <form onSubmit={submit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="firstName">Prénom</Label>
          <Input id="firstName" value={values.firstName} onChange={set('firstName')} autoComplete="given-name" />
          {errors.firstName && <p className="text-sm text-danger">{errors.firstName}</p>}
        </div>
        <div>
          <Label htmlFor="lastName">Nom</Label>
          <Input id="lastName" value={values.lastName} onChange={set('lastName')} autoComplete="family-name" />
          {errors.lastName && <p className="text-sm text-danger">{errors.lastName}</p>}
        </div>
        <div>
          <Label htmlFor="password">Mot de passe</Label>
          <PasswordInput id="password" value={values.password} onChange={set('password')} autoComplete="new-password" />
          {errors.password && <p className="text-sm text-danger">{errors.password}</p>}
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
          <PasswordInput id="confirmPassword" value={values.confirmPassword} onChange={set('confirmPassword')} autoComplete="new-password" />
          {errors.confirmPassword && <p className="text-sm text-danger">{errors.confirmPassword}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={accept.isPending}>
          {accept.isPending ? 'Création du compte…' : 'Accepter l’invitation'}
        </Button>
      </form>
    </InvitationShell>
  );
}
